export type NfseFieldStatus =
  | "required"
  | "optional"
  | "hidden"
  | "unsupported"
  | "displayOnly";

export type NfseReferenceSource = "local" | "cache" | "focusFuture";

export interface NfseFieldProfile {
  status: NfseFieldStatus;
  helpKey: string;
}

export interface NfseReferenceProfile {
  source: NfseReferenceSource;
  key: string;
}

export interface NfseMunicipalProfile {
  id: string;
  municipalityCode: string;
  municipalityName: string;
  uf: string;
  provider: "focus_nfse";
  fields: Record<string, NfseFieldProfile>;
  referenceData: Record<string, NfseReferenceProfile>;
}

export const NFSE_SAO_PAULO_PROFILE_ID = "BR-SP-3550308";
export const SAO_PAULO_MUNICIPALITY_CODE = "3550308";

export const saoPauloNfseProfile = {
  id: NFSE_SAO_PAULO_PROFILE_ID,
  municipalityCode: SAO_PAULO_MUNICIPALITY_CODE,
  municipalityName: "Sao Paulo",
  uf: "SP",
  provider: "focus_nfse",
  fields: {
    municipal_registration: {
      status: "required",
      helpKey: "fiscalSettings.nfse.help.municipalRegistration",
    },
    municipal_code: {
      status: "required",
      helpKey: "fiscalSettings.nfse.help.municipalCode",
    },
    focus_api_token: {
      status: "required",
      helpKey: "fiscalSettings.nfse.help.focusToken",
    },
    nfse_service_list_item: {
      status: "required",
      helpKey: "itemForm.fiscal.nfse.help.serviceListItem",
    },
    nfse_iss_rate: {
      status: "required",
      helpKey: "itemForm.fiscal.nfse.help.issRate",
    },
    nfse_natureza_operacao: {
      status: "optional",
      helpKey: "itemForm.fiscal.nfse.help.operationNature",
    },
    nfse_service_code: {
      status: "unsupported",
      helpKey: "itemForm.fiscal.nfse.help.serviceCodeUnsupportedSaoPaulo",
    },
    nfse_cnae: {
      status: "unsupported",
      helpKey: "itemForm.fiscal.nfse.help.cnaeUnsupportedSaoPaulo",
    },
  },
  referenceData: {
    nfse_service_list_item: {
      source: "local",
      key: "sao-paulo-service-list",
    },
  },
} satisfies NfseMunicipalProfile;

export const nfseMunicipalProfiles = [saoPauloNfseProfile] as const;

export function getNfseMunicipalProfile(
  municipalityCode?: string | null,
): NfseMunicipalProfile {
  return (
    nfseMunicipalProfiles.find(
      (profile) => profile.municipalityCode === municipalityCode,
    ) ?? saoPauloNfseProfile
  );
}

export function getNfseFieldStatus(
  profile: NfseMunicipalProfile,
  field: string,
): NfseFieldStatus {
  return profile.fields[field]?.status ?? "optional";
}

export function isNfseFieldRequired(
  profile: NfseMunicipalProfile,
  field: string,
): boolean {
  return getNfseFieldStatus(profile, field) === "required";
}

export function isNfseFieldSupported(
  profile: NfseMunicipalProfile,
  field: string,
): boolean {
  const status = getNfseFieldStatus(profile, field);

  return status !== "unsupported" && status !== "hidden";
}
